/**
 * Procedural galaxies → star systems → planets for hyperjump travel.
 */
import { getPlanetIcon } from './icons.js';
import { generateStationStock } from './items.js';

const GALAXY_NAMES = [
    'Andromeda Reach', 'Orion Spur', 'Cygnus Veil', 'Perseus Arm',
    'Helix Expanse', 'Sagitta Drift', 'Lyra Cluster', 'Vela Void',
    'Crux Nexus', 'Phoenix Sector', 'Hydra Fold', 'Draco Rim',
];

const SYSTEM_PREFIX = [
    'Kepler', 'Nova', 'Rigel', 'Vega', 'Altair', 'Sirius', 'Procyon',
    'Deneb', 'Polaris', 'Antares', 'Aldebaran', 'Castor', 'Pollux',
    'Arcturus', 'Spica', 'Regulus', 'Fomalhaut', 'Achernar',
];

// colors: [mid, dark, light] for planet icon + sky tints
// sky: deep void color, accent for grid/mist, star tint, mist density 0-1
const PLANET_TYPES = [
    {
        type: 'terran', weight: 2, colors: ['#3a8f5c', '#2d6b4a', '#8fd4a8'],
        sky: { bg: '#040c10', accent: '#0a3a28', star: '#c8ffe0', mist: 0.35, mistColor: '40,120,80', stars: 140 },
    },
    {
        type: 'desert', weight: 2, colors: ['#c4a35a', '#8a6b30', '#e8d090'],
        sky: { bg: '#100a04', accent: '#3a2810', star: '#ffe8b0', mist: 0.55, mistColor: '160,110,40', stars: 90 },
    },
    {
        type: 'ice', weight: 2, colors: ['#a8d4e8', '#5a8aaa', '#e8f6ff'],
        sky: { bg: '#040a14', accent: '#1a3a55', star: '#e0f4ff', mist: 0.5, mistColor: '140,190,230', stars: 160 },
    },
    {
        type: 'lava', weight: 1, colors: ['#cc4422', '#661100', '#ff8844'],
        sky: { bg: '#120404', accent: '#4a1208', star: '#ffaa66', mist: 0.65, mistColor: '180,40,10', stars: 70 },
    },
    {
        type: 'gas', weight: 2, colors: ['#c9a06a', '#8a6040', '#e8c898'],
        sky: { bg: '#0c0806', accent: '#3a2818', star: '#ffd8a0', mist: 0.8, mistColor: '140,90,50', stars: 50 },
    },
    {
        type: 'toxic', weight: 1, colors: ['#66aa33', '#335511', '#aaff66'],
        sky: { bg: '#060c04', accent: '#1a3a08', star: '#ccff88', mist: 0.7, mistColor: '80,160,20', stars: 80 },
    },
    {
        type: 'ocean', weight: 2, colors: ['#2266aa', '#0a3355', '#66aadd'],
        sky: { bg: '#030810', accent: '#0a2848', star: '#a8d8ff', mist: 0.4, mistColor: '30,90,160', stars: 130 },
    },
    {
        type: 'storm', weight: 1, colors: ['#6655aa', '#332255', '#aa99ff'],
        sky: { bg: '#080610', accent: '#2a1848', star: '#d0b8ff', mist: 0.75, mistColor: '90,50,160', stars: 100 },
    },
    {
        type: 'crystal', weight: 1, colors: ['#88ddff', '#226688', '#ccf0ff'],
        sky: { bg: '#040a12', accent: '#0a3858', star: '#c0f0ff', mist: 0.3, mistColor: '60,180,220', stars: 200 },
    },
    {
        type: 'barren', weight: 2, colors: ['#888888', '#444444', '#bbbbbb'],
        sky: { bg: '#060608', accent: '#222228', star: '#dde0e8', mist: 0.15, mistColor: '100,100,110', stars: 180 },
    },
    {
        type: 'jungle', weight: 1, colors: ['#228844', '#0a4422', '#66cc88'],
        sky: { bg: '#030a06', accent: '#0a2a14', star: '#b8ffcc', mist: 0.6, mistColor: '20,100,50', stars: 110 },
    },
    {
        type: 'metal', weight: 1, colors: ['#99aabb', '#445566', '#ccddee'],
        sky: { bg: '#06080c', accent: '#1a2838', star: '#c8d8ee', mist: 0.25, mistColor: '80,100,130', stars: 150 },
    },
];

const STATION_NAMES = [
    'Port Helios', 'Dock Sigma', 'Outpost K-7', 'Hab Ring Theta',
    'Bazaar Nova', 'Relay Voss', 'Yard 19', 'Citadel Meridian',
    'Freeport Dust', 'Ark Spire', 'Terminal Zenith', 'Market Deep',
];

function weightedPick(list) {
    const total = list.reduce((s, x) => s + x.weight, 0);
    let r = Math.random() * total;
    for (const x of list) {
        r -= x.weight;
        if (r <= 0) return x;
    }
    return list[0];
}

function uid(prefix) {
    return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

function makePlanet(systemId, index) {
    const t = weightedPick(PLANET_TYPES);
    const [mid, dark, light] = t.colors;
    const name = `${pickSyllable()}${pickSyllable()}-${index + 1}`;
    const hasStation = Math.random() < 0.55 || index === 0;
    const hasRings = t.type === 'gas' || Math.random() < 0.15;
    const sky = t.sky || {
        bg: '#03040a', accent: dark, star: '#cfe8ff', mist: 0.2, mistColor: '40,60,90', stars: 100,
    };
    // Slight per-planet variation so siblings of same type still differ
    const mistJitter = (Math.random() - 0.5) * 0.15;
    const starJitter = Math.floor((Math.random() - 0.5) * 40);
    const planet = {
        id: uid('pl'),
        systemId,
        index,
        name,
        type: t.type,
        color: mid,
        colorDark: dark,
        colorLight: light,
        hasRings,
        ringColor: light + '99',
        danger: +(0.3 + Math.random() * 0.7).toFixed(2),
        wealth: +(0.2 + Math.random() * 0.8).toFixed(2),
        hasStation,
        stationName: hasStation ? STATION_NAMES[Math.floor(Math.random() * STATION_NAMES.length)] : null,
        stock: null, // generated on visit
        // Map position within system (polar)
        orbit: 40 + index * 28 + Math.random() * 10,
        angle: Math.random() * Math.PI * 2,
        size: 0.7 + Math.random() * 0.6,
        bgAccent: sky.accent,
        bgColor: sky.bg,
        atmosphere: {
            bg: sky.bg,
            accent: sky.accent,
            starColor: sky.star,
            mist: Math.max(0, Math.min(0.95, sky.mist + mistJitter)),
            mistColor: sky.mistColor,
            starCount: Math.max(40, sky.stars + starJitter),
            // drifting mist phase seed
            mistSeed: Math.random() * 1000,
            // occasional bright “local star” tint in sky
            flare: Math.random() < 0.35,
            flareColor: light,
        },
    };
    planet.icon = getPlanetIcon(planet);
    return planet;
}

function pickSyllable() {
    const a = ['Al', 'Bel', 'Cor', 'Dra', 'El', 'Fen', 'Gor', 'Hel', 'Ix', 'Jar', 'Kel', 'Lun', 'Mor', 'Nex', 'Or', 'Py', 'Quel', 'Ra', 'Sol', 'Tor', 'Um', 'Vex', 'Wyn', 'Xan', 'Yor', 'Zed'];
    const b = ['a', 'e', 'i', 'o', 'u', 'ar', 'en', 'is', 'on', 'us', 'ax', 'or'];
    return a[Math.floor(Math.random() * a.length)] + b[Math.floor(Math.random() * b.length)];
}

function makeSystem(galaxyId, index) {
    const id = uid('sys');
    const planetCount = 3 + Math.floor(Math.random() * 5);
    const planets = [];
    for (let i = 0; i < planetCount; i++) planets.push(makePlanet(id, i));
    return {
        id,
        galaxyId,
        index,
        name: `${SYSTEM_PREFIX[Math.floor(Math.random() * SYSTEM_PREFIX.length)]}-${100 + Math.floor(Math.random() * 900)}`,
        starColor: ['#ffeeaa', '#ffaa66', '#aaccff', '#ff6688', '#ffffff'][Math.floor(Math.random() * 5)],
        planets,
        // Position on galaxy map
        x: 40 + Math.random() * 220,
        y: 40 + Math.random() * 160,
    };
}

function makeGalaxy(index) {
    const id = uid('gal');
    const systemCount = 4 + Math.floor(Math.random() * 4);
    const systems = [];
    for (let i = 0; i < systemCount; i++) systems.push(makeSystem(id, i));
    return {
        id,
        index,
        name: GALAXY_NAMES[index % GALAXY_NAMES.length],
        hue: Math.floor(Math.random() * 360),
        systems,
    };
}

export class Cosmos {
    constructor(galaxyCount = 4) {
        this.galaxies = [];
        for (let i = 0; i < galaxyCount; i++) {
            this.galaxies.push(makeGalaxy(i));
        }
        // Start location
        const g0 = this.galaxies[0];
        const s0 = g0.systems[0];
        const p0 = s0.planets[0];
        this.current = {
            galaxyId: g0.id,
            systemId: s0.id,
            planetId: p0.id,
        };
        this._ensureStationStock(p0);
    }

    getGalaxy(id = this.current.galaxyId) {
        return this.galaxies.find(g => g.id === id);
    }

    getSystem(systemId = this.current.systemId) {
        for (const g of this.galaxies) {
            const s = g.systems.find(x => x.id === systemId);
            if (s) return s;
        }
        return null;
    }

    getPlanet(planetId = this.current.planetId) {
        for (const g of this.galaxies) {
            for (const s of g.systems) {
                const p = s.planets.find(x => x.id === planetId);
                if (p) return p;
            }
        }
        return null;
    }

    getCurrentLocation() {
        const galaxy = this.getGalaxy();
        const system = this.getSystem();
        const planet = this.getPlanet();
        return { galaxy, system, planet };
    }

    /** Fuel cost to jump to a planet */
    fuelCost(toPlanetId) {
        const from = this.getPlanet();
        const to = this.getPlanet(toPlanetId);
        if (!from || !to) return 99;
        if (from.id === to.id) return 0;

        const fromSys = this.getSystem(from.systemId);
        const toSys = this.getSystem(to.systemId);
        if (from.systemId === to.systemId) {
            // Local hop
            return 5 + Math.abs(from.index - to.index) * 3;
        }
        // Inter-system
        let cost = 18;
        if (fromSys.galaxyId !== toSys.galaxyId) cost += 25; // inter-galaxy
        cost += Math.floor(Math.hypot(fromSys.x - toSys.x, fromSys.y - toSys.y) / 20);
        return cost;
    }

    canTravel(toPlanetId, fuel) {
        const cost = this.fuelCost(toPlanetId);
        return { ok: fuel >= cost && cost > 0, cost };
    }

    travelTo(planetId) {
        const planet = this.getPlanet(planetId);
        if (!planet) return null;
        const system = this.getSystem(planet.systemId);
        this.current = {
            galaxyId: system.galaxyId,
            systemId: system.id,
            planetId: planet.id,
        };
        this._ensureStationStock(planet);
        return planet;
    }

    _ensureStationStock(planet) {
        if (!planet.hasStation) return;
        if (!planet.stock || planet.stock.length === 0) {
            const luck = planet.wealth;
            planet.stock = generateStationStock(10 + Math.floor(planet.wealth * 8), luck);
            // Price adjust by wealth
            for (const item of planet.stock) {
                item.price = Math.round(item.price * (0.85 + planet.wealth * 0.4));
            }
        }
    }

    refreshStation(planetId) {
        const p = this.getPlanet(planetId);
        if (!p) return;
        p.stock = null;
        this._ensureStationStock(p);
    }
}
